import React, { useEffect, useRef } from 'react';
import Chart from "chart.js/auto";
import { CategoryScale, LinearScale, BarController, BarElement, LineController, LineElement, PointElement, PieController, ArcElement} from 'chart.js';

Chart.register(CategoryScale, LinearScale, BarController, BarElement, LineController, LineElement, PointElement, PieController, ArcElement);

const defaultChartColors = [
	'rgba(255, 99, 132, 0.2)',
	'rgba(54, 162, 235, 0.2)',
	'rgba(255, 206, 86, 0.2)',
	'rgba(75, 192, 192, 0.2)',
	'rgba(153, 102, 255, 0.2)',
	'rgba(255, 159, 64, 0.2)'
];

const legendOptions = {
	rtl: true,
	labels: {
		font: {
			family: 'IranYekan',
			size: 10,
		},
		textAlign: 'right',
		padding: 12,
		boxWidth: 10,
		color: 'black'
	},
};

const hiddenScale = {
	beginAtZero: true,
	grid: {
		display: false
	},
	ticks: {
		display: false,
		precision: 0
	},
	border: {
		display: false
	},
	title: false,
};

const categoryScale = (showTicks) => ({
	grid: {
		display: false
	},
	border: {
		display: false
	},
	ticks: {
		display: showTicks,
		font: {
			family: 'IranYekan',
			size: 10,
		},
		color: 'black',
	},
	title: true,
});

const GetStackedBorderRadius = (context) => {
	const { dataIndex, datasetIndex, chart } = context;
	const datasets = chart.data.datasets || [];
	let first = -1;
	let last = -1;
	datasets.forEach((dataset, index) => {
		if (Number(dataset.data?.[dataIndex] || 0) > 0)
		{
			if (first === -1)
			{
				first = index;
			}
			last = index;
		}
	});
	if (datasetIndex !== first && datasetIndex !== last)
	{
		return 0;
	}
	if (first === last)
	{
		return 7;
	}
	if (datasetIndex === first)
	{
		return { topLeft: 7, bottomLeft: 7, topRight: 0, bottomRight: 0 };
	}
	return { topLeft: 0, bottomLeft: 0, topRight: 7, bottomRight: 7 };
};

const Barchart = ({labels, data, type, stackedData}) =>
{
	const chartRef = useRef(null);
	const chartInstance = useRef(null);
	const isStacked = type === 'stackedbarchart';
	useEffect(() => {
		CreateChart();
		return () => {
			if (chartInstance.current)
			{
				chartInstance.current.destroy();
			}
		};
	// eslint-disable-next-line
	}, []);

	const ChartType = (ChartType) =>
	{
		switch (ChartType)
		{
			case 'barchart': ChartType = 'bar'; break;
			case 'stackedbarchart': ChartType = 'bar'; break;
			case 'linechart': ChartType = 'line'; break;
			case 'piechart': ChartType = 'pie'; break;
			default: break;
		}
		return ChartType;
	}

	const GetChartColors = (data) =>
	{
		let colors = [];
		if (Array.isArray(Object.values(data)))
		{
			for(const value of data)
			{
				if(value["color"])
				{
					colors[colors.length] = value["color"];
				}
			}
		}

		if(colors.length === 0)
		{
			colors = defaultChartColors;
		}

		return colors;
	}

	const GetChartValues = (data) =>
	{
		let result = [];
		if (Array.isArray(Object.values(data)))
		{
			for(const value of data)
			{
				if(typeof value == "object" && value["value"])
				{
					result[result.length] = value["value"];
				}
				else
				{
					result[result.length] = value;
				}
			}
		}

		return result;
	}

	const GetChartLabels = (data, labels, type) => {
		if (labels && labels.length > 0)
		{
			return labels;
		}

		if (Array.isArray(data))
		{
			return data.map(value => {
				if (typeof value === "object" && value["label"] !== undefined) 
				{
					return ChartType(type) === "pie"
						? `${value["label"]}(${Math.round(value["value"])}%)`
						: value["label"];
				}
				return '';
			});
		}
		return [];
	};

	const GetAnimation = () => {
		let delayed;
		return {
			onComplete: () => {
				delayed = true;
			},
			delay: (context) => {
				let delay = 0;
				if (context.type === 'data' && context.mode === 'default' && !delayed)
				{
					delay = context.dataIndex * 500 + context.datasetIndex * 100;
				}
				return delay;
			},
		};
	};

	const CreateStackedChart = (ctx) =>
	{
		const series = stackedData?.series || [];
		const statusLabels = stackedData?.labels || [];
		chartInstance.current = new Chart(ctx, {
			type: 'bar',
			data: {
				labels: statusLabels,
				datasets: series.map((item, index) => ({
					label: item.label,
					data: Array.isArray(item.data) ? item.data : Object.values(item.data || {}),
					backgroundColor: defaultChartColors[index % defaultChartColors.length],
					borderColor: defaultChartColors[index % defaultChartColors.length],
					borderRadius: GetStackedBorderRadius,
					borderWidth: 0,
					borderSkipped: false,
				})),
			},
			options: {
				indexAxis: 'y',
				responsive: true,
				maintainAspectRatio: false,
				plugins: {
					legend: {
						display: true,
						position: 'bottom',
						...legendOptions,
					},
					tooltip: {
						rtl: true,
						filter: (item) => item.raw > 0,
					},
				},
				animation: GetAnimation(),
				scales: {
					x: {
						...hiddenScale,
						stacked: true,
					},
					y: {
						...categoryScale(true),
						stacked: true,
						position: 'right',
					},
				},
			}
		});
	};

	const CreateChart = () =>
	{
		const ctx = chartRef.current.getContext('2d');
		if (isStacked)
		{
			CreateStackedChart(ctx);
			return;
		}
		chartInstance.current = new Chart(ctx, {
			type: ChartType(type),
			data: {
				labels: GetChartLabels(data, labels, type),
				datasets: [{
					label: '',
					data: GetChartValues(data),
					backgroundColor: GetChartColors(data),
					borderColor: GetChartColors(data),
					borderRadius: ChartType(type) == "pie" ? 0 : 7,
					borderWidth: 0,
					tension: 0.4,
				}]
			},
			options: {
				responsive: true,
				maintainAspectRatio: false,
				plugins: {
					legend: {
						position: ChartType(type) == "pie" ? 'bottom' : false,
						...legendOptions,
					},
				},
				elements: {
					arc: {
						display:false,
						borderWidth: 0
					}
				},
				animation: GetAnimation(),
				scales: {
					y: hiddenScale,
					x: categoryScale(ChartType(type) !== "pie"),
				}
			}
		});
	};
	return (
		<div style={{ position: 'relative', height: isStacked ? '220px' : '160px', width: '100%' }}>
			<canvas ref={chartRef} />
		</div>
	);
};

export default Barchart;
